<!-- eslint-disable prefer-destructuring -->
<!-- eslint-disable max-len -->
<template>
  <div class="conference-booking-modal">
    <CustomModal :show="show"
                 static-modal
                 @close="onClose">
      <template #header>
        <div class="d-flex flex-column">
          <h1 class="custom-modal__heading">
            <template v-if="edit">
              Edit
            </template>

            <template v-if="wishlistId">
              Wishlist Booking
            </template>
            <template v-else>
              Booking
            </template>
          </h1>
        </div>
      </template>

      <div class="conference-booking-modal__body">
        <div v-if="error">
          <h3>Error</h3>

          <p>
            Something has gone wrong whilst trying to process your request. Please try again later.
          </p>

          <p>
            If this issue persists, please contact us.
          </p>
        </div>

        <div v-else-if="showErrorModal">
          <h3>Error</h3>
          <p>
            {{ bookingApiErrorMsg }}
          </p>
        </div>

        <div v-else-if="loading">
          <LoadingSpinner></LoadingSpinner>
        </div>

        <div v-else-if="fullyBooked">
          <h3>Error</h3>

          <p>This conference is fully booked.</p>
        </div>

        <div v-else-if="instance && webAccountId">
          <template v-if="timedOut">
            <h2 class="conference-booking-modal__subheading">
              Your booking has expired
            </h2>

            <p>
              If you still wish to book {{ conferenceTitle }}, you will need to restart the booking
              process.
            </p>
          </template>

          <template v-else>
            <template v-if="step < 1">
              <h2 class="conference-booking-modal__subheading">
                You are booking {{ conferenceTitle }}
              </h2>

              <div class="conference-booking-modal__details">
                <div>
                  <span class="conference-booking-modal__item-heading">
                    Date:
                  </span>

                  {{ instance.marketingStartDate }}
                </div>
                <div v-if="instance.marketingLocation">
                  <span class="conference-booking-modal__item-heading">
                    Location:
                  </span>

                  {{ instance.marketingLocation }}
                </div>
                <div v-if="instance.priceExcVat">
                  <span class="conference-booking-modal__item-heading">
                    Price:
                  </span>

                  {{ instance.priceExcVat }}
                </div>
              </div>
            </template>

            <template v-if="step === 0 && paidTeacherPlaces > 0">
              <p class="my-2 conference-booking-modal__info-text">
                We need to know the details of the teachers attending. These can be amended later.
                You can copy and paste the same details if you do not know who will be attending
                and amend at a later date.
              </p>
              <p>Enter delegate details for your paid teacher places.</p>
              <ul class="conference-booking-modal__delegates-list">
                <li v-for="i in paidTeacherPlaces"
                    :key="i">
                  <EventBookingDelegate v-model="delegates[i - 1]"
                                        :index="i"
                                        show-validity
                                        :show-dietary-requirements="instance.showDietaryRequirements"
                                        :show-disability-options="instance.showDisabilityOptions"
                                        :disabilities="disabilities"
                                        :dietary-requirements="dietaryRequirements"
                                        :valid="delegateValidity[i - 1] || inWishlist"
                                        :allow-removal="inWishlist"
                                        :edit="edit"
                                        :readonly="readonly"
                                        :delegate-metadata="delegateMetadata"
                                        :hide-add-details-later="hideAddDetailsLater"
                                        @metadata-valid="val => onDelegateMetadataValid(val, i - 1)"
                                        @remove="onDelegateRemove(i - 1)">
                  </EventBookingDelegate>
                </li>
              </ul>

              <div v-if="!isValid && submitAttempted && !inWishlist"
                   class="validation-error">
                Attendee details will be required in order to checkout this product
                <!-- , or tick the box to skip adding the details until later -->
              </div>
            </template>
            <template v-if="step === 0 && freeTeacherPlaces > 0">
              <p>Enter delegate details for your free teacher places.</p>
              <ul class="conference-booking-modal__delegates-list">
                <li v-for="j in freeTeacherPlaces"
                    :key="j">
                  <EventBookingDelegate v-model="delegates[j - 1 + paidTeacherPlaces]"
                                        :index="j"
                                        show-validity
                                        :show-dietary-requirements="instance.showDietaryRequirements"
                                        :show-disability-options="instance.showDisabilityOptions"
                                        :disabilities="disabilities"
                                        :dietary-requirements="dietaryRequirements"
                                        :job-types="jobTypes"
                                        :valid="delegateValidity[j - 1 + paidTeacherPlaces] || inWishlist"
                                        :allow-removal="inWishlist"
                                        :edit="edit"
                                        :readonly="readonly"
                                        :delegate-metadata="delegateMetadata"
                                        :hide-add-details-later="hideAddDetailsLater"
                                        @metadata-valid="val => onDelegateMetadataValid(val, j - 1 + paidTeacherPlaces)"
                                        @remove="onDelegateRemove(j - 1 + paidTeacherPlaces)">
                  </EventBookingDelegate>
                </li>
              </ul>

              <div v-if="!isValid && submitAttempted && !inWishlist"
                   class="validation-error">
                Attendee details will be required in order to checkout this product
                <!-- , or tick the box to skip adding the details until later -->
              </div>
            </template>
            <template v-if="step === 1">
              <template v-if="remainingTime || isWishlistBooking || inWishlist">
                <h2 class="conference-booking-modal__subheading">
                  <template v-if="edit">
                    Your booking has been updated
                  </template>
                  <template v-else>
                    <template v-if="isWishlistBooking">
                      Your booking has been added to the wishlist
                    </template>
                    <template v-else>
                      Your booking has been added to the basket
                    </template>
                  </template>
                </h2>

                <p v-if="!isWishlistBooking && !inWishlist">
                  Please complete your purchase within
                  {{ remainingTime }}

                  <template v-if="remainingTime === 1">
                    minute
                  </template>
                  <template v-else>
                    minutes
                  </template>

                  to secure your place.
                </p>
              </template>
            </template>
          </template>
        </div>

        <div v-else>
          <p>Please log in to continue.</p>
          <a class="btn btn-accent-1"
             href="/login">
            Log in
          </a>
        </div>
      </div>

      <template #footer>
        <template v-if="!error && webAccountId">
          <button v-if="timedOut"
                  class="btn btn-accent-1"
                  @click="onRestart">
            Start again
          </button>

          <button v-else-if="fullyBooked"
                  class="btn btn-accent-1"
                  @click="onJoinWaitingList">
            Join waiting list
          </button>

          <button v-else-if="step < 1"
                  :disabled="loading"
                  class="btn btn-accent-1"
                  @click="onContinue">
            <template v-if="edit && !startedAgain">
              Save changes
            </template>
            <template v-else
                      :disabled="true">
              Next
            </template>
          </button>

          <button v-else-if="step === 1"
                  class="btn btn-accent-1"
                  @click="onContinue">
            <template v-if="edit">
              <template v-if="inWishlist">
                Back to wishlist
              </template>
              <template v-else>
                Back to basket
              </template>
            </template>
            <template v-else>
              <template v-if="isWishlistBooking">
                Go to wish list
              </template>
              <template v-else>
                Go to basket
              </template>
            </template>
          </button>
        </template>
      </template>
    </CustomModal>
  </div>
</template>

<script>
import { BasketResource } from "spiritweb.packages.ecommerce";
import CustomModal from "~/Components/CustomModal.vue";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";
import EventBookingDelegate from "~/Components/Events/EventBookingDelegate.vue";
import { bookConference, abandonConference } from "../../Scripts/resources/conferenceResource";
import {
  updateBooking,
  saveDelegates,
  listDietaryRequirements,
  listDisabilities,
  listJobTypes,
  loadBooking,
  loadEventDelegateMetadata,
  saveDelegateMetadata,
  loadEventBookingDelegateMetadata,
  listBookings,
} from "~/Scripts/resources/eventsResource";

// #region types
/**
 * @typedef {Object} Address
 * @property {string} addressId UUID
 * @property {string} orgId UUID
 * @property {string} orgName
 * @property {string} line1
 * @property {string} line2
 * @property {string} line3
 * @property {string} town
 * @property {string} region
 * @property {string} postcode
 * @property {number} [countryId]
 * @property {string} country
 */

/**
 * @typedef {Object} Session
 * @property {string} name
 * @property {string} description
 * @property {string} endDateTime Date is already a formatted string
 * @property {string} startDateTime Date is already a formatted string
 * @property {Address} address
 * @property {boolean} limitNumberOfSpaces
 * @property {boolean} [mandatory]
 * @property {number} [maxSpaces]
 * @property {number} [spacesAvailable]
 * @property {number} [spacesBooked]
 */

/**
 * @typedef {Object} conferenceInstance
 * @property {string} id UUID
 * @property {string} conferenceId UUID
 * @property {string} [defaultSessionId] UUID
 * @property {number} [productId]
 * @property {boolean} showLocation
 * @property {boolean} showDisabilityOptions
 * @property {boolean} showDietaryRequirements
 * @property {string} location
 * @property {string} duration
 * @property {string} startDate Date is already a formatted string
 * @property {string} endDate Date is already a formatted string
 * @property {string} address
 * @property {string} postcode
 * @property {string} priceExVat
 * @property {number} [vatRate] double
 * @property {boolean} applyVat
 * @property {number} productId
 * @property {number} [spacesAvailable]
 * @property {number} [minDelegates]
 * @property {number} [maxDelegates]
 * @property {number} [maxDelegatesPerOrganisation]
 * @property {number} [maxDelegatesOnWaitingList]
 * @property {Array<Session>} sessions
 * @property {string} instanceType
 */
// #endregion types

export default {
  name: "ConferenceBookingModal",
  components: {
    CustomModal,
    EventBookingDelegate,
    LoadingSpinner,
  },
  props: {
    productId: {
      type: Number,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    show: {
      type: Boolean,
      default: false,
    },
    /** @type {import('vue').PropType<EventInstance>} */
    instance: {
      type: Object,
      default: () => ( {} ),
    },
    conferenceTitle: {
      type: String,
      default: null,
    },
    webAccountId: {
      type: String,
      default: null,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    wishlistId: {
      type: String,
      default: null,
    },
    inWishlist: {
      type: Boolean,
      default: false,
    },
    bookingStatus: {
      type: String,
      default: null,
    },
    orgId: {
      type: String,
      default: null,
    },
    basketUrl: {
      type: String,
      default: null,
    },
    hideAddDetailsLater: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    loadBookingMetadata: {
      type: Boolean,
      default: false,
    },
    paidTeacherPlaces: {
      type: Number,
      default: 0,
    },
    freeTeacherPlaces: {
      type: Number,
      default: 0,
    },
    products: {
      type: Array,
      default: () => ( [] ),
    },
    instanceId: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      step: 0,
      selectedQuantity: null,
      delegates: [],
      submitAttempted: false,
      bookingId: null,
      holdExpiryDateTime: null,
      remainingTime: null,
      interval: null,
      disabilities: [],
      dietaryRequirements: [],
      jobTypes: [],
      error: false,
      loading: false,
      startedAgain: false,
      fullyBooked: false,
      showDietaryRequirements: undefined,
      showDisabilityOptions: undefined,
      delegateMetadata: null,
      metadataValidity: [],
      bookingApiErrorCode: 100,
      bookingApiErrorMsg: null,
      bookingIdPaid: null,
      bookingsData: null,
      teacherOrderLine: null,
      studentOrderLine: null,
      freeOrderLine: null,
      orderLineIds: [],
      showErrorModal: false,
    };
  },
  computed: {
    totalTeachers () {
      return this.paidTeacherPlaces + this.freeTeacherPlaces;
    },
    delegateValidity () {
      const result = [];
      const mandatory = ["firstName", "lastName", "emailAddress"];

      this.delegates.forEach( delegate => {
        let delegateValid = false;
        // eslint-disable-next-line
        const mandatoryFields = Object.entries(delegate).filter(([key]) => mandatory.find( y => y === key ) );
        delegateValid = mandatoryFields.every( ( [, value] ) => value );

        if ( !this.emailIsValid( delegate.emailAddress ) ) {
          delegateValid = false;
        }

        result.push( delegateValid );
      } );

      return result;
    },
    isValid () {
      let result = false;

      if ( this.step === 0 ) {
        result = true;
        const hasMetadata = this.metadataValidity.length > 0;
        for ( let i = 0; i < this.delegateValidity.length; i += 1 ) {
          const d = this.delegateValidity[i];
          const m = this.metadataValidity[i] || !hasMetadata;
          if ( ( ( d && m ) === false ) && this.submitAttempted ) {
            console.log( "delegate validity: ", d );
            console.log( "metadata validity: ", m );
            console.log( "delegate number: ", i );
          }
          result = result && ( ( d && m ) );
        }
      } else {
        result = true;
      }

      return result;
    },
    isWishlistBooking () {
      return this.wishlistId !== null && this.wishlistId !== undefined;
    },
    timedOut () {
      let result = false;

      if ( this.remainingTime !== null && !this.inWishlist ) {
        result = this.remainingTime <= 0;
      }
      return result;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler ( val ) {
        this.bookingId = val;
      },
    },
    instance: {
      deep: true,
      immediate: true,
      async handler ( val ) {
        if ( val ) {
          const {
            id, showDietaryRequirements, showDisabilityOptions, eventInstanceId,
          } = val;

          this.showDietaryRequirements = showDietaryRequirements;
          this.showDisabilityOptions = showDisabilityOptions;

          if ( showDietaryRequirements ) {
            await this.setDietaryRequirementsList();
          }
          if ( showDisabilityOptions ) {
            await this.setDisabilitiesList();
          }

          this.delegateMetadata = (
            await loadEventDelegateMetadata( id || eventInstanceId ) )?.data;

          if ( this.loadBookingMetadata ) {
            const metadata = (
              await loadEventBookingDelegateMetadata( this.bookingId ) )?.data;
            Object.keys( metadata ).forEach( key => {
              const d = this.delegates.find( x => x.id === key );
              const m = metadata[key];
              for ( let i = 0; i < m.length; i += 1 ) {
                d[m[i].label] = m[i].value;
              }
            } );
          }

          await this.setJobTypes( id || eventInstanceId );
        } else {
          this.disabilities = [];
          this.dietaryRequirements = [];
          this.jobTypes = [];
        }
      },
    },
    bookingId: {
      immediate: true,
      handler ( val ) {
        this.$emit( "input", val );
        if ( val && this.edit ) {
          this.loading = true;

          loadBooking( val ).then( response => {
            if ( response.data ) {
              const delegates = response.data.delegates.map( x => this.mapDelegate( x ) );

              if ( delegates?.length > 0 ) {
                this.delegates = delegates;
              } else if ( this.startedAgain ) {
                this.delegates = this.delegates.map( x => {
                  const { id, ...rest } = x;

                  return {
                    id: null,
                    ...rest,
                  };
                } );
              }

              this.orderLineId = response.data.orderLineId;
              this.holdExpiryDateTime = response.data.holdExpiryDateTime;
              this.selectedQuantity = response.data.numberOfDelegates;

              this.step = 1;

              this.loading = false;
            }
          } );
        }
      },
    },
    totalTeachers: {
      immediate: true,
      handler ( val ) {
        // Remove excess delegate records when the list length exceeds total teacher places
        while ( this.delegates.length > val ) {
          this.delegates.pop();
        }
      },
    },
    step () {
      this.submitAttempted = false;
    },
    holdExpiryDateTime ( val ) {
      const clearInterval = () => {
        window.clearInterval( this.interval );
        this.interval = null;
      };

      const setRemainingTime = () => {
        const time = this.getRemainingTime();

        if ( time > 0 ) {
          this.remainingTime = time;
        } else {
          this.remainingTime = 0;
          clearInterval();
        }
      };

      setRemainingTime();

      if ( val ) {
        if ( !this.interval ) {
          this.interval = window.setInterval( setRemainingTime, 1000 );
        }
      } else {
        clearInterval();
      }
    },
    async timedOut ( val ) {
      if ( val && !this.edit ) {
        await this.removeItem( this.freeOrderLine );
        await this.removeItem( this.teacherOrderLine );
        await this.removeItem( this.studentOrderLine );
      }
    },
  },
  errorCaptured () {
    this.error = true;
  },
  methods: {
    async fetch ( apiPromise, ...args ) {
      return new Promise( ( resolve, reject ) => {
        apiPromise( ...args )
          .then( response => {
            const { status, data } = response;
            if ( status === 200 && data ) {
              resolve( data );
            } else {
              throw new Error( "Bad response." );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    async setJobTypes ( id ) {
      return this.fetch( listJobTypes, id )
        .then( response => {
          this.jobTypes = response;
        } )
        .catch( console.error );
    },
    async setDietaryRequirementsList () {
      return this.fetch( listDietaryRequirements )
        .then( response => {
          this.dietaryRequirements = response;
        } )
        .catch( console.error );
    },
    async setDisabilitiesList () {
      return this.fetch( listDisabilities )
        .then( response => {
          this.disabilities = response;
        } )
        .catch( console.error );
    },
    mapDelegate ( delegate ) {
      const {
        eventDelegateId: id,
        dietaryRequirements,
        disabilities,
        unallocated: addDetailsLater,
        ...rest
      } = delegate;
      // eslint-disable-next-line max-len
      const dietaryRequirementIds = dietaryRequirements?.map( x => x.eventDietaryRequirementId );

      const disabilityIds = disabilities?.map( x => x.eventDisabilityId );

      return {
        id,
        dietaryRequirementIds,
        disabilityIds,
        addDetailsLater,
        ...rest,
      };
    },
    getRemainingTime () {
      let result = 10; // minutes

      if ( this.holdExpiryDateTime ) {
        // Hold Expiry Time is already UTC minus the time zone data so the browser will tack on
        // the clients time zone, there for we need to strip it out
        const exDate = this.removeTimeZone( new Date( this.holdExpiryDateTime ) );

        // Get te current date from the client as UTC
        // const date = this.convertToUtc( new Date() );
        const date = new Date();

        result = Math.floor( ( exDate - date ) / 1000 / 60 );
      }

      if ( result < 0 ) {
        result = 0;
      }

      return result;
    },
    convertToUtc ( date ) {
      return Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
      );
    },
    removeTimeZone ( date ) {
      return Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
      );
    },
    removeItem ( id ) {
      return new Promise( ( resolve, reject ) => {
        const payload = { orderLineId: id };
        this.$root.$emit( "sw::removeFromBasket::request", payload );
        BasketResource.removeItem( id )
          .then( response => {
            this.$root.$emit( "sw::removeFromBasket::success", response );
            resolve();
          } )
          .catch( error => {
            this.$root.$emit( "sw::removeFromBasket::failure", error );
            reject( error );
          } )
          .finally( () => {
            this.$root.$emit( "sw::removeFromBasket::response", payload );
          } );
      } );
    },
    emailIsValid ( email ) {
      return /\S+@\S+\.\S+/.test( email );
    },
    async saveDelegates () {
      const delegatesPaid = this.delegates.slice( 0, this.paidTeacherPlaces )
        .map( x => ( {
          ...x,
          sendConfirmationEmails: false,
          eventDelegateTypeId: 1,
        } ) );
      const delegatesFree = this.delegates
        .slice( this.paidTeacherPlaces, this.paidTeacherPlaces + this.freeTeacherPlaces )
        .map( x => ( {
          ...x,
          sendConfirmationEmails: false,
          eventDelegateTypeId: 1,
        } ) );

      let freeBookingId = "";
      let paidBookingId = "";
      let data = "";
      console.log( "bookingsData", this.bookingsData );
      if ( this.freeOrderLine != null && this.freeTeacherPlaces > 0 ) {
        data = this.bookingsData.filter(
          x => ( x.eventInstanceId === this.instance.eventInstanceId
        || x.eventInstanceId === this.instance.id )
        && x.orderLineId === this.freeOrderLine
        && x.numberOfDelegates === this.freeTeacherPlaces );
        freeBookingId = data[0].eventBookingId;
        console.log( "freeId", freeBookingId, data );
      } else {
        // The data takes a different format than normal with just 1 paid booking place
        freeBookingId = this.bookingsData[0].eventBookingId;
      }
      if ( this.paidTeacherPlaces > 0 && this.teacherOrderLine != null ) {
        data = this.bookingsData.filter(
          x => ( x.eventInstanceId === this.instance.eventInstanceId
          || x.eventInstanceId === this.instance.id )
          && x.orderLineId === this.teacherOrderLine
          && x.numberOfDelegates === this.paidTeacherPlaces );
        paidBookingId = data[0].eventBookingId;
        console.log( "paidId", paidBookingId, data );
      } else if ( this.paidTeacherPlaces > 0 ) {
        // eslint-disable-next-line max-len
        // If there are no student places, the student order line id will actually be the paid teacher one
        paidBookingId = freeBookingId;
      }

      const metadata = {};
      if ( this.paidTeacherPlaces > 0 ) {
        const delegateResultPaid = await saveDelegates( paidBookingId, delegatesPaid );
        if ( delegateResultPaid?.data ) {
          for ( let i = 0; i < delegateResultPaid.data.length; i += 1 ) {
            const id = delegateResultPaid.data[i];
            const values = [];
            if ( this.delegateMetadata ) {
              const m = { ...this.delegateMetadata[0] };
              m.value = this.delegates[i][this.delegateMetadata[0].label];
              values.push( m );
              metadata[id] = values;
            }
          }
        }
      }

      if ( this.freeTeacherPlaces > 0 ) {
        const delegateResultFree = await saveDelegates( freeBookingId, delegatesFree );
        if ( delegateResultFree?.data ) {
          for ( let i = 0; i < delegateResultFree.data.length; i += 1 ) {
            const id = delegateResultFree.data[i];
            const values = [];
            console.log( " del met", this.delegateMetadata );
            if ( this.delegateMetadata ) {
              const j = i + this.paidTeacherPlaces;
              const m = { ...this.delegateMetadata[0] };
              console.log( "delegate loop", j, this.delegates[j], this.delegateMetadata[0].label );
              m.value = this.delegates[j][this.delegateMetadata[0].label];
              values.push( m );
            }
            metadata[id] = values;
          }
        }
      }
      if ( metadata ) {
        console.log( "metadata save", metadata );
        await saveDelegateMetadata( metadata );
      }
    },
    updateBooking () {
      let delegateCount = 0;
      const promises = [];
      if ( this.delegates ) {
        delegateCount = this.delegates.length;
      }
      promises[0] = new Promise( ( resolve, reject ) => {
        updateBooking(
          this.instance.eventInstanceId || this.instance.id,
          this.value,
          this.webAccountId,
          this.bookingStatus,
          this.teacherOrderLine,
          this.orgId,
          this.paidTeacherPlaces,
        )
          .then( response => {
            if ( response.data ) {
              resolve();
            }
          } )
          .catch( e => {
            reject( e );
          } );
      } );
      promises[1] = new Promise( ( resolve, reject ) => {
        updateBooking(
          this.instance.eventInstanceId || this.instance.id,
          this.value,
          this.webAccountId,
          this.bookingStatus,
          this.freeOrderLine,
          this.orgId,
          delegateCount,
        )
          .then( response => {
            if ( response.data ) {
              resolve();
            }
          } )
          .catch( e => {
            reject( e );
          } );
      } );
    },
    onDelegateRemove ( index ) {
      this.selectedQuantity -= 1;
      this.delegates.splice( index, 1 );
    },
    async modalBookConference () {
      this.loading = true;

      try {
        const { data } = await bookConference(
          this.instanceId,
          this.products,
          this.productId,
          this.freeTeacherPlaces );

        if ( data?.Booked ) {
          console.log( "book data return", data );
          this.orderLineIds = data?.OrderLineIds;
          if ( this.paidTeacherPlaces > 0 && this.freeTeacherPlaces > 0 ) {
            [this.studentOrderLine, this.teacherOrderLine, this.freeOrderLine] = this.orderLineIds;
          } else if ( this.paidTeacherPlaces > 0 && this.freeTeacherPlaces === 0 ) {
            [this.teacherOrderLine] = this.orderLineIds;
            this.freeOrderLine = null;
            this.studentOrderLine = null;
          } else {
            [this.studentOrderLine, this.freeOrderLine] = this.orderLineIds;
            this.teacherOrderLine = null;
          }

          this.refreshBasket();
          this.showModal = true;
        } else if ( data?.HasError ) {
          this.handleApiError( data.ErrorCode, data.ErrorMessage );
          this.showErrorModal = true;
        } else {
          this.bookingApiErrorMsg = "Unexpected booking result";
          this.showErrorModal = true;
        }
      } catch ( err ) {
        this.bookingApiErrorMsg = err;
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    async onContinue () {
      this.submitAttempted = true;
      console.log( "this ", this );
      if ( this.step === 0 ) {
        if ( this.isValid ) {
          console.log( "delegate continue logging", this.delegateMetadata );
          this.step += 1;
          await this.modalBookConference();
          await listBookings()
            .then( response => {
              this.bookingsData = response.data;
              let data = this.bookingsData;
              if ( this.bookingsData !== null ) {
                data = this.bookingsData.filter(
                  x => ( x.eventInstanceId === this.instance.eventInstanceId
                    || x.eventInstanceId === this.instance.id )
                    && ( x.orderLineId === this.teacherOrderLine
                      || x.orderLineId === this.studentOrderLine ) );
              } else {
                this.error = true;
              }
              if ( data.length > 0 ) {
                this.holdExpiryDateTime = data[0].holdExpiryDateTime;
              } else {
                this.holdExpiryDateTime = this.bookingsData[0].holdExpiryDateTime;
              }
            } );
          if ( this.isValid || this.inWishlist ) {
            this.saveDelegates()
              .then( () => {
                if ( this.inWishlist ) {
                  return this.updateBooking();
                }
                return Promise.resolve();
              } )
              .catch( e => {
                this.error = e;
                this.showErrorModal = true;
              } );
          }
        }
      } else if ( this.step === 1 ) {
        if ( this.edit ) {
          this.onClose();
        } else if ( this.isWishlistBooking ) {
          window.location = `/wishlists/${this.wishlistId}`;
        } else if ( this.basketUrl ) {
          window.location = this.basketUrl;
        }
      }
    },
    onRestart () {
      this.paidBookingId = null;
      this.freeBookingId = null;
      this.holdExpiryDateTime = null;
      this.remainingTime = null;
      this.submitAttempted = false;

      if ( !this.edit ) {
        this.teacherOrderLine = null;
        this.freeOrderLine = null;
        this.studentOrderLine = null;
      }

      this.$emit( "restart" );

      this.step = 0;
      this.startedAgain = true;
    },
    onJoinWaitingList () {
      const { id } = this.instance;

      this.$emit( "close" );
      this.resetState();
      this.$emit( "join", id, this.selectedQuantity );
    },
    onClose () {
      if ( this.bookingId && !this.edit && this.step < 1 ) {
        abandonConference( this.orderLineIds )
          .then( () => this.removeItem( this.freeOrderLine ) )
          .then( () => this.removeItem( this.teacherOrderLine ) )
          .then( () => this.removeItem( this.studentOrderLine ) )
          .then( () => {
            this.$emit( "close" );
            this.resetState();
          } )
          .catch( e => {
            this.error = e;
            this.showErrorModal = true;
          } );
      } else {
        this.$emit( "close" );
        this.resetState();
      }
    },
    /**
    * Refresh any basket components on the page
    */
    refreshBasket () {
      this.$root.$emit( "sw::refreshBasket::request" );
    },
    resetState () {
      this.bookingId = null;
      this.orderLineId = null;
      this.holdExpiryDateTime = null;
      this.startedAgain = false;
      this.step = 0;
      this.delegates = [];
      this.bookingApiErrorCode = 100;
      this.bookingApiErrorMsg = null;
      this.bookingIdPaid = null;
      this.bookingsData = null;
      this.teacherOrderLine = null;
      this.studentOrderLine = null;
      this.freeOrderLine = null;
      this.submitAttempted = false;
    },
    onDelegateMetadataValid ( valid, index ) {
      this.$set( this.metadataValidity, index, valid );
    },
    handleApiError ( apiCode, apiMessage ) {
      this.bookingApiErrorCode = apiCode;

      switch ( +apiCode ) {
        case 120:
          this.bookingApiErrorMsg = "Due to the number of places in other customers’ baskets, there are currently insufficient places available to fulfil this order. Please try again later.";
          break;
        default:
          this.bookingApiErrorMsg = apiMessage || "An unexpected booking error has occured.";
      }
    },
  },
};
</script>

<style lang="less" scoped>
  /deep/ .custom-modal__header {
    align-items: unset;
  }

  .conference-booking-modal {
    &__info-text

  {
    color: #000;
  }

  &__subheading {
    font-size: 16px;
    color: #000;
    padding: 15px 0;
  }

  &__message {
    display: block;
    margin-bottom: 1rem;
  }

  &__details {
    margin-bottom: 15px;
  }

  &__item-heading {
    font-weight: 600;
  }

  &__num-delegates {
    max-width: 300px;
    .form-group

  {
    margin-bottom: 0;
  }

  }

  &__delegates-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__add-another {
    color: #0c499c;
    padding: 0;
    border: 0;
    background: none;
    font-size: 14px;
    &:hover

  {
    color: darken(#0c499c, 15%);
    text-decoration: underline;
  }

  }
  }

  .custom-modal {
    &__heading

  {
    strong

  {
    color: #000;
  }

  }
  }
</style>
